import { Dispatch, SetStateAction } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { UseFormSetValue } from "react-hook-form";
import { CollectorDTO } from "api";
import { UtilityBillFormData } from "types";
import { QUERY_KEYS } from "consts";
import { compareCollector } from "pages/authenticated/documents/utils";

interface UseCollectorHandlersParams {
    collectorsFromSiteData?: CollectorDTO[];
    setValue: UseFormSetValue<UtilityBillFormData>;
    setIsCollectorEmpty: Dispatch<SetStateAction<boolean>>;
    setNewCollectorId: Dispatch<SetStateAction<number | undefined>>;
    currentSite: { id: number | undefined; name: string | null };
    queryClient: ReturnType<typeof useQueryClient>;
}

export const useCollectorHandlers = ({
    collectorsFromSiteData,
    setValue,
    setIsCollectorEmpty,
    setNewCollectorId,
    currentSite,
    queryClient,
}: UseCollectorHandlersParams) => {
    const handleCollectorChange = (collectorString: string | null) => {
        setIsCollectorEmpty(false);

        if (!collectorString || typeof collectorString !== 'string') {
            return;
        }

        const collectorsSource = collectorsFromSiteData || [];
        const selectedCollector = collectorsSource.find((c: any) =>
            compareCollector(collectorString, c)
        );

        if (selectedCollector) {
            // Update all relevant fields immediately with shouldDirty flag
            setValue("collector", collectorString, { shouldDirty: true });
            setValue("siteName", selectedCollector.siteName || "", { shouldDirty: true });
            setValue("provider", selectedCollector.providerName || "", { shouldDirty: true });
            setValue("measuredUnit", selectedCollector.consumptionUnit || "", { shouldDirty: true });
            setValue("currency", selectedCollector.currency || "", { shouldDirty: true });
            setValue("accountNumber", selectedCollector.accountNumber || "", { shouldDirty: true });
            setValue("meterNumber", selectedCollector.meterNumber || "", { shouldDirty: true });
            setValue("utilityTypeName", selectedCollector.utilityTypeName || "", { shouldDirty: true });
            setValue("subtype", selectedCollector.utilitySubType || "", { shouldDirty: true });
        }
    };

    const handleSetNewCollectorId = (collectorId: SetStateAction<number | undefined>) => {
        setNewCollectorId(collectorId);
        if (currentSite.id) {
            queryClient.invalidateQueries([
                QUERY_KEYS.COLLECTORS,
                QUERY_KEYS.SITES,
                currentSite.id,
            ]);
        }
    };

    return {
        handleCollectorChange,
        handleSetNewCollectorId,
    };
};

